import React, { useState } from 'react';
import { Box, Chip, Stack, Typography, Tooltip, Paper, Grid, Button, IconButton } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import PreviewPicture from '../../../components/PreviewPic/PreviewPicture';
import { baseUrl } from '../../../util/constant';

const vehicleTitle = (v) => [
  v?.vehicleType?.name,
  v?.year,
  v?.make?.name,
  v?.model?.name,
].filter(Boolean).join(' ');

const vehicleId = (v) => v?.vin || v?.chassisNumber || v?.chassisNo || v?.serial || '-';
const vehicleColor = (v) => v?.color?.name || v?.color || null;
const vehiclePrice = (v) => v?.price ?? v?.unitPrice ?? null;

const Dot = ({ color = '#9ca3af' }) => (
  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, display: 'inline-block', mr: 0.5 }} />
);

const ContainerHeader = ({ container }) => {
  const cNum = container?.containerNumber || 'N/A';
  const seal = container?.sealNumber || '-';
  const size = container?.containerSize || container?.size || null;
  const weight = container?.netWeight != null ? `${container.netWeight}` : null;
  const vehiclesCount = (container?.shipmentItems || []).length;
  return (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
      <Inventory2Icon fontSize="small" sx={{ color: '#0B3C5D' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0B3C5D' }}>Container #{cNum}</Typography>
      {size && <Chip size="small" variant="outlined" label={size} />}
      <Chip size="small" variant="outlined" label={`Seal ${seal}`} />
      {weight && <Chip size="small" variant="outlined" label={`${weight} kg`} />}
      <Chip size="small" color="primary" label={`${vehiclesCount} vehicles`} />
    </Stack>
  );
};

const imageFormats = ['.jpg', '.jpeg', '.png', '.gif'];
const isImage = (file) => {
  if (!file?.extension) return false;
  const ext = String(file.extension).toLowerCase();
  return imageFormats.some((fmt) => ext.includes(fmt));
};

const VehicleCard = ({ v, onPreview }) => {
  const title = vehicleTitle(v) || 'Vehicle';
  const id = vehicleId(v);
  const color = vehicleColor(v);
  const price = vehiclePrice(v);
  const pictures = v?.warehousePictures || v?.picture || v?.pictures || [];
  const firstImage = pictures.find(isImage);
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 1.5, position: 'relative' }}>
      <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
        <Tooltip title={pictures.length ? 'View images' : 'No images'}>
          <span>
            <IconButton size="small" onClick={() => onPreview && onPreview(pictures)} disabled={!pictures.length}>
              <PhotoLibraryIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          {firstImage ? (
            <Box sx={{ width: 56, height: 44, borderRadius: 1, overflow: 'hidden', bgcolor: '#f3f4f6', border: '1px solid #e5e7eb' }}>
              <img src={`${baseUrl}${firstImage.filePath}`} alt={firstImage.fileName} width="56" height="44" style={{ objectFit: 'cover', display: 'block' }} />
            </Box>
          ) : (
            <DirectionsCarIcon fontSize="small" sx={{ color: '#6b7280' }} />
          )}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" variant="outlined" label={id} />
          {color && (
            <Chip
              size="small"
              variant="outlined"
              label={color}
              icon={<Dot color={typeof color === 'string' ? undefined : '#9ca3af'} />}
            />
          )}
          {price != null && (
            <Tooltip title="Declared value">
              <Chip size="small" color="default" label={`$${Number(price).toLocaleString()}`} />
            </Tooltip>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

const ContainersList = ({ containers = [], compact = false, maxVehiclesPerContainer = 3, enableShowMore = true }) => {
  const [expanded, setExpanded] = useState({});
  const [openPreview, setOpenPreview] = useState(false);
  const [previewFiles, setPreviewFiles] = useState([]);
  const handleOpenPreview = (files) => { setPreviewFiles(files || []); setOpenPreview(true); };
  const handleClosePreview = () => { setOpenPreview(false); setPreviewFiles([]); };
  const toggle = (key) => setExpanded((m) => ({ ...m, [key]: !m[key] }));

  // layout hint if needed later: const gridCols = useMemo(() => ({ xs: 1, sm: 2, md: compact ? 2 : 3 }), [compact]);

  return (
    <Stack spacing={1.5}>
      {containers.map((c) => {
        const key = c.id || c.containerNumber || Math.random();
        const vehicles = c?.shipmentItems?.map((item) => item.vehicle) || [];
        const isExpanded = !!expanded[key];
        const sliceCount = compact && !isExpanded ? maxVehiclesPerContainer : vehicles.length;
        const showVehicles = vehicles.slice(0, sliceCount);
        const hidden = Math.max(vehicles.length - showVehicles.length, 0);

        return (
          <Paper key={key} variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: '#fcfcfd', borderColor: '#e5e7eb' }}>
            <ContainerHeader container={c} />
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={1}>
        {showVehicles.map((v, idx) => (
                  <Grid key={v.id || v.vin || v.chassisNumber || idx} item xs={12} sm={6} md={compact ? 6 : 4}>
          <VehicleCard v={v} onPreview={handleOpenPreview} />
                  </Grid>
                ))}
                {vehicles.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">No vehicles</Typography>
                  </Grid>
                )}
              </Grid>
              {enableShowMore && hidden > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Button size="small" onClick={() => toggle(key)}>{isExpanded ? 'Show less' : `Show ${hidden} more`}</Button>
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}
      {(!containers || containers.length === 0) && (
        <Typography variant="caption" color="text.secondary">No containers</Typography>
      )}
      {openPreview && (
        <PreviewPicture open={openPreview} handleClose={handleClosePreview} label={'Vehicle Pictures'} file={previewFiles} />
      )}
    </Stack>
  );
};

export default ContainersList;
